import { Component, EventEmitter, Input, Output, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastService } from '../../../trash/shared/components';
import { Subscription } from 'rxjs';
import { FormInputConfig, ListValues } from '../../../trash/shared/interfaces/form-input-config';
import { PublicService, removeFalsyKeys } from '../../../trash/shared/services/public.service';
import {
  CitiesApiClientProvider,
  CountriesApiClientProvider,
  CreateAddressModel,
  ErrorOrError,
  handleApiErrors,
  IAddressRequest,
  ICitiesApiClient,
  ICityResponse,
  ICountriesApiClient,
  ICountriesResponse,
  ICreateAddressReqeust,
  IGeographicalZoneResponse,
  IPaginationResponse,
  IStateResponse,
  IStatesApiClient,
  StatesApiClientProvider
} from '../../../common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { FormComponent } from '../../../trash/shared/components/dynamic-form/form/form.component';
@Component({
  selector: 'app-address-step',
  standalone: true,
  imports: [
    TranslateModule,
    ButtonModule,
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    FormsModule,
    FormComponent
],
  templateUrl: './address-step.component.html',
  styleUrls: ['./address-step.component.scss'],
})
export class AddressStepComponent implements OnDestroy, OnInit {
  private subscriptions: Subscription[] = [];
  private toastService = inject(ToastService);
  private translate = inject(TranslateService);
  private publicService = inject(PublicService);
  private _CountriesApiClient: ICountriesApiClient = inject(CountriesApiClientProvider).getClient();
  private _StatesApiClient: IStatesApiClient = inject(StatesApiClientProvider).getClient();
  private _CitiesApiClient: ICitiesApiClient = inject(CitiesApiClientProvider).getClient();

  @Input() validationEmit: boolean = false;
  @Input() formConfig: FormInputConfig[] = [];
  @Input() title: string = 'GENERAL.HQ_ADDRESS';
  @Input() showBackButton: boolean = true;
  @Input() backButtonText: string = 'GENERAL.BACK';
  @Input() nextButtonText: string = 'GENERAL.NEXT';
  @Input() saveButtonText: string = 'GENERAL.SAVE';
  @Input() isLastStep: boolean = false;
  @Input() addressData: any;
  @Input() addressModel: CreateAddressModel = CreateAddressModel.createDefault();
  countries: ListValues[] = [];
  states: ListValues[] = [];

  @Output() onChange = new EventEmitter<Record<string, unknown>>();
  @Output() onSubmit = new EventEmitter<IAddressRequest>();
  @Output() onBack = new EventEmitter<void>();

  constructor() {}

  ngOnInit(): void {
    this.getCountries();
    if (this.addressData) {
      this.onCountryChange(this.addressData?.country?.id || this.addressData?.countryId);
      this.onStateChange(this.addressData?.state?.id || this.addressData?.stateId);
      this.onCityChange(this.addressData?.city?.id || this.addressData?.cityId);
    }

  }

  private getCountries(): void {
    const subscription = this._CountriesApiClient.get({pageSize: 10000}).subscribe({
      next: (response: IPaginationResponse<ICountriesResponse>) => {
        this.countries = response.items.map(item => ({
          label: this.publicService.getCurrentLang() == 'en'? item.englishName : item.arabicName,
          value: item.id
        }));
        this.initFormWithData();
      },
      error: (error) => handleApiErrors(error, this.toastService)
    });
    this.subscriptions.push(subscription);
  }

  private initFormWithData(): void {
    this.formConfig = this.addressModel.getAddressFormConfig(
      this.addressData,
      this.countries,
      true,
      this.translate,
      (countryId: string) => this.onCountryChange(countryId),
      (stateId: string) => this.onStateChange(stateId),
      (cityId: string) => this.onCityChange(cityId)
    );
  }

  handleChange(data: any): void {
    this.onChange.emit(data);
  }

  private onCountryChange(countryId: string): void {
    if (!countryId) return;
    // this.getGeographicalZonesByCountryId(countryId);
    this.getStatesByCountryId(countryId);
  }

  private onStateChange(stateId: string): void {
    if (!stateId) return;
    this.getCitiesByStateId(stateId);
  }

  private onCityChange(cityId: string): void {
    if (!cityId) return;
    this.getDistrictsByCityId(cityId);
  }

  // private getGeographicalZonesByCountryId(countryId: string): void {
  //   const subscription = this._CountriesApiClient.getGeographicalZones(countryId).subscribe({
  //     next: (response: IPaginationResponse<IGeographicalZoneResponse>) => {
  //       this.addressModel.updateListValues(response.items, 'geographicalZones', 'geographicalZoneId');
  //     },
  //     error: (error: ErrorOrError[]) => handleApiErrors(error, this.toastService)
  //   });
  //   this.subscriptions.push(subscription);
  // }

  private getStatesByCountryId(countryId: string): void {
    const subscription = this._CountriesApiClient.getStates(countryId, {pageSize: 10000}).subscribe({
      next: (response: IPaginationResponse<IStateResponse>) => {
        this.addressModel.updateListValues(response.items, 'states', 'stateId');
      },
      error: (error: ErrorOrError[]) => handleApiErrors(error, this.toastService)
    });
    this.subscriptions.push(subscription);
  }

  private getCitiesByStateId(stateId: string): void {
    const subscription = this._StatesApiClient.getCities(stateId, {pageSize: 10000}).subscribe({
      next: (response: IPaginationResponse<ICityResponse>) => {
        this.addressModel.updateListValues(response.items, 'cities', 'cityId');
      },
      error: (error: ErrorOrError[]) => handleApiErrors(error, this.toastService)
    });
    this.subscriptions.push(subscription);
  }

  private getDistrictsByCityId(cityId: string): void {
    const subscription = this._CitiesApiClient.getDistricts(cityId, {pageSize: 10000}).subscribe({
      next: (response: IPaginationResponse<ICityResponse>) => {
        this.addressModel.updateListValues(response.items, 'districts', 'districtId');
      },
      error: (error: ErrorOrError[]) => handleApiErrors(error, this.toastService)
    });
    this.subscriptions.push(subscription);
  }

  handleSubmit(data: any): void {
    const cleanData = removeFalsyKeys(data);
    this.onSubmit.emit(cleanData);
  }

  handleBack(): void {
    this.onBack.emit();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }
}
